import React, { useEffect, useState } from 'react';
import { Button, Form, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { useAddCandidateNote } from '../../common/useAddCandidateNote';
import { useUpdateCandidateNote } from '../../common/useUpdateCandidateNote';
import { CandidateNotesList } from '../../components/CandidateNotesList';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { AddIcon, EditButtons } from '../../components/TableWithDataUpdate';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';
import { NoteType } from '../recruitments/Recruitments.types';

import { useDeleteCandidateNote } from './useDeleteCandidateNote';

const Notes = styled(StyledColumn)`
  height: 100%;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
  }
`;

export type CandidateNotes = {
  candidateId: string;
  notes: NoteType[];
};

export const CandidateNotes: React.FC<CandidateNotes> = ({ candidateId, notes }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(0);
  const queryClient = useQueryClient();
  const addNote = useAddCandidateNote();
  const deleteNote = useDeleteCandidateNote();
  const updateNote = useUpdateCandidateNote();

  const [form] = useForm();

  const sortedNotes = notes.sort((a, b) => b.createdDate - a.createdDate);

  useEffect(() => {
    if (isEditing) {
      const note = notes.find((note) => note.id === isEditing)?.content;
      form.setFieldValue('note', note);
    } else {
      form.setFieldValue('note', null);
    }
  }, [isEditing, form, notes]);

  const invalidateCandidateQuery = () =>
    queryClient.invalidateQueries(pathToUrl(ApiRoutesEnum.getCandidate, { id: candidateId }));

  const handleSave = () => {
    if (!candidateId) return;
    const values = form.getFieldsValue(true);
    addNote.mutate(
      { queryParams: { id: candidateId }, data: { content: values.note } },
      {
        onSuccess: () => {
          invalidateCandidateQuery();
          setIsAdding(false);
        },
      }
    );
  };

  const handleUpdateNote = () => {
    if (!candidateId) return;
    const values = form.getFieldsValue(true);
    updateNote.mutate(
      { queryParams: { candidateId, noteId: isEditing }, data: { content: values.note } },
      {
        onSuccess: () => {
          invalidateCandidateQuery();
          setIsEditing(0);
        },
      }
    );
  };

  const handleDeleteNote = (noteId: number) => {
    deleteNote.mutate(
      { queryParams: { candidateId, noteId } },
      {
        onSuccess: invalidateCandidateQuery,
      }
    );
  };

  const handleCancel = () => {
    setIsAdding(false);
    setIsEditing(0);
  };

  const handleAddNote = () => {
    setIsAdding(true);
  };

  const handleEditNote = (noteId: number) => {
    setIsEditing(noteId);
  };

  const AddNote: React.FC = () => {
    return (
      <Form form={form} colon={false} layout="vertical" style={{ width: '100%', marginTop: '10px' }}>
        <FormTextArea name="note" rows={18} />
      </Form>
    );
  };

  return (
    <StyledColumn height="670px" justifyContent="flex-start" gap="0">
      <StyledRow justifyContent="space-between">
        <Typography.Title level={5} style={{ paddingLeft: '15px' }}>
          Notes
        </Typography.Title>
        {isAdding || isEditing ? (
          <EditButtons handleCancel={handleCancel} handleSave={isEditing ? handleUpdateNote : handleSave} />
        ) : (
          <StyledRow flexGrow="0">
            <Button icon={<AddIcon />} onClick={handleAddNote}>
              Add
            </Button>
          </StyledRow>
        )}
      </StyledRow>
      <Notes>
        {isAdding || isEditing ? (
          <AddNote />
        ) : (
          <CandidateNotesList onDelete={handleDeleteNote} onEdit={handleEditNote} notes={sortedNotes} />
        )}
      </Notes>
    </StyledColumn>
  );
};
